import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchFscHouseholdsQuery, useSearchCrmClientsQuery, useHubspotSearchClientsQuery } from '../../app/api';
import { fscPickerPalette as work } from './FscHouseholdPicker';

/* "Existing client?" search for the new-portal-client wizard.
 *
 * One box searches Salesforce households and the merging firms' CRMs (clients
 * and prospects), and the results are combined into ONE ROW PER HOUSEHOLD -
 * the operator is looking for a family, not choosing between systems. A row
 * merges:
 *   * a Salesforce household, with
 *   * every CRM person matched to it (crosswalk), or failing that,
 *   * the CRM people who share a CRM household (Redtail family).
 *   * a HubSpot contact (marketing lead) whose email matches someone in the
 *     row; a lead nobody else knows becomes a row of its own.
 * Each row says which systems it was found in, and "Select" hands the whole
 * group to the wizard ({ fsc, people, hubspot }), which links the Salesforce
 * household and prefills from the CRM people or the lead.
 *
 * Both endpoints are office-scoped on the server. Either source can be left
 * out once it has been picked.
 */

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

const chip = (bg, color) => ({
  background: bg, color, borderRadius: 4, padding: '0.05rem 0.4rem',
  fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.3px', whiteSpace: 'nowrap',
});

// Salesforce households first (with their matched CRM people), then CRM
// households nobody in Salesforce is matched to, in search order.
function groupResults(households, people, leads = []) {
  const groups = households.map(h => ({ key: `fsc:${h.fsc_id}`, fsc: h, people: [] }));
  const byFsc = new Map(groups.map(g => [g.fsc.fsc_id, g]));
  const byCrmHousehold = new Map();
  people.forEach(p => {
    const viaFsc = p.fsc_household_id && byFsc.get(p.fsc_household_id);
    if (viaFsc) { viaFsc.people.push(p); return; }
    const k = p.household_key ? `crm:${p.household_key}` : `person:${p.id}`;
    let g = byCrmHousehold.get(k);
    if (!g) {
      g = { key: k, fsc: null, people: [] };
      byCrmHousehold.set(k, g);
      groups.push(g);
    }
    g.people.push(p);
  });
  // A lead joins the row of whoever shares its email; otherwise it stands alone.
  leads.forEach(l => {
    const em = (l.email || '').toLowerCase();
    const g = em && groups.find(x => !x.hubspot && x.people.some(p => (p.email || '').toLowerCase() === em));
    if (g) g.hubspot = l;
    else groups.push({ key: `hs:${l.hubspot_contact_id}`, fsc: null, people: [], hubspot: l });
  });
  // The client before the spouse within a row, as the wizard will order them.
  groups.forEach(g => g.people.sort((a, b) => (b.is_client ? 1 : 0) - (a.is_client ? 1 : 0)));
  return groups;
}

function HouseholdRow({ group, onSelect, existingEmails }) {
  const { fsc, people, hubspot } = group;
  const first = people[0];
  const leadName = hubspot && [hubspot.first_name, hubspot.last_name].filter(Boolean).join(' ');
  const title = fsc?.name || first?.household_name || first?.display_name || fsc?.fsc_id || leadName || hubspot?.email;
  const sources = [...new Set(people.map(p => p.source_name).filter(Boolean))];
  const advisor = fsc?.rep_name || people.find(p => p.advisor_name)?.advisor_name;
  const office = fsc?.rep_firm_name || (!fsc && sources[0]);
  const place = people.find(p => p.city || p.state);
  const leadOnly = hubspot && !fsc && people.length === 0;
  const hasPortal = Boolean(fsc?.has_portal)
    || Boolean(leadOnly && hubspot.email && existingEmails.has(hubspot.email.toLowerCase()));
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
      padding: '0.7rem 0.85rem', borderBottom: `1px solid ${work.hairline}`, background: 'white',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: work.ink, fontSize: '0.92rem' }}>{title}</span>
          {fsc && <span style={chip('rgba(0,105,166,0.1)', work.tealBlue)}><i className="bi bi-check2 me-1" />Salesforce</span>}
          {sources.map(s => (
            <span key={s} style={chip('rgba(26,127,78,0.1)', work.green)}><i className="bi bi-check2 me-1" />{s} CRM</span>
          ))}
          {hubspot && <span style={chip('rgba(208,155,44,0.15)', '#8a5a00')}><i className="bi bi-check2 me-1" />HubSpot{leadOnly ? ' lead' : ''}</span>}
          {hasPortal && <span style={chip(work.tealBlue, 'white')}>Already has portal</span>}
        </div>
        {people.length > 0 && (
          <div style={{ color: work.ink, fontSize: '0.8rem', marginTop: 3 }}>
            {people.map((p, i) => (
              <span key={p.id}>
                {i > 0 && <span style={{ color: work.muted }}> · </span>}
                {p.display_name}
                {p.status && <span style={{ color: work.muted }}> ({p.status})</span>}
              </span>
            ))}
          </div>
        )}
        {leadOnly && (
          <div style={{ color: work.ink, fontSize: '0.8rem', marginTop: 3 }}>
            {[hubspot.company, hubspot.lead_status && `Lead status ${hubspot.lead_status.replace(/_/g, ' ').toLowerCase()}`]
              .filter(Boolean).join(' · ') || 'Marketing lead, not yet a client'}
          </div>
        )}
        <div style={{ color: work.muted, fontSize: '0.76rem', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '0.2rem 0.9rem' }}>
          {leadOnly && hubspot.email && <span><i className="bi bi-envelope me-1" />{hubspot.email}</span>}
          {leadOnly && hubspot.phone && <span><i className="bi bi-telephone me-1" />{hubspot.phone}</span>}
          {advisor && <span><i className="bi bi-person-badge me-1" />{advisor}</span>}
          {office && <span><i className="bi bi-building me-1" />{office}</span>}
          {place && <span><i className="bi bi-geo-alt me-1" />{[place.city, place.state].filter(Boolean).join(', ')}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onSelect(group)}
        style={{
          flexShrink: 0, border: 0, borderRadius: 4, padding: '0.4rem 0.95rem',
          fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
          ...(hasPortal
            ? { background: work.hairline, color: work.muted }
            : { background: work.goldenrod, color: work.deepBlue }),
        }}
      >
        Select
      </button>
    </div>
  );
}
HouseholdRow.propTypes = {
  group: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  existingEmails: PropTypes.instanceOf(Set).isRequired,
};

export default function ExistingClientSearch({
  showFsc = true, showCrm = true, showHubspot = false, existingEmails = new Set(), onSelect, onCancel,
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const ready = debounced.length >= MIN_QUERY;
  const fsc = useSearchFscHouseholdsQuery(
    { q: debounced, limit: 8, letter_field: 'last_name' },
    { skip: !ready || !showFsc },
  );
  const crm = useSearchCrmClientsQuery(
    { q: debounced, status_group: 'all', limit: 12 },
    { skip: !ready || !showCrm },
  );
  // Live HubSpot API call (no local copy), so a slow or unconfigured HubSpot
  // must never hold up or break the other two sources.
  const hs = useHubspotSearchClientsQuery({ q: debounced }, { skip: !ready || !showHubspot });
  const households = showFsc ? (fsc.data?.households || []) : [];
  const people = showCrm ? (crm.data?.clients || []) : [];
  const leads = showHubspot && hs.data?.configured ? (hs.data.results || []) : [];
  const groups = groupResults(households, people, leads);
  // HubSpot only holds up the list when nothing else has come back yet.
  const loading = (showFsc && fsc.isFetching) || (showCrm && crm.isFetching)
    || (groups.length === 0 && showHubspot && hs.isFetching);
  const failed = (showFsc && fsc.isError) || (showCrm && crm.isError);
  const truncated = (showFsc && (fsc.data?.total || 0) > households.length)
    || (showCrm && (crm.data?.total || 0) > people.length);

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by last name, email or phone…"
          style={{
            flex: '1 1 260px', minWidth: 220, border: `1px solid ${work.hairline}`, borderRadius: 4,
            padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: work.ink, background: 'white', outline: 'none',
          }}
        />
        <button type="button" onClick={onCancel} style={{
          background: 'transparent', color: work.deepBlue, border: `1px solid ${work.hairlineStrong}`,
          borderRadius: 4, padding: '0.45rem 0.85rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
        }}>
          Cancel
        </button>
      </div>

      {!ready ? (
        <p style={{ color: work.muted, fontSize: '0.78rem', margin: '0.5rem 0 0' }}>
          Type at least {MIN_QUERY} characters. Each result is one household, combined from
          {' '}{[showFsc && 'Salesforce', showCrm && "the merging firms' CRMs", showHubspot && 'HubSpot leads']
            .filter(Boolean).join(', ').replace(/, ([^,]*)$/, ' and $1')}.
        </p>
      ) : failed ? (
        <p style={{ color: work.red, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>Could not search. Try again in a moment.</p>
      ) : loading ? (
        <p style={{ color: work.muted, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>Searching…</p>
      ) : groups.length === 0 ? (
        <p style={{ color: work.muted, fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
          No existing client matches &ldquo;{debounced}&rdquo;. If they are new, close this and fill in the household below.
        </p>
      ) : (
        <div style={{ marginTop: '0.6rem', border: `1px solid ${work.hairline}`, borderRadius: 4, overflow: 'hidden', maxHeight: 380, overflowY: 'auto' }}>
          {groups.map(g => <HouseholdRow key={g.key} group={g} onSelect={onSelect} existingEmails={existingEmails} />)}
          {showHubspot && hs.isFetching && (
            <div style={{ padding: '0.45rem 0.85rem', color: work.muted, fontSize: '0.75rem', background: work.canvas }}>
              Still checking HubSpot…
            </div>
          )}
          {truncated && (
            <div style={{ padding: '0.45rem 0.85rem', color: work.muted, fontSize: '0.75rem', background: work.canvas }}>
              More matches than shown - narrow the search to see the rest.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
ExistingClientSearch.propTypes = {
  showFsc: PropTypes.bool,
  showCrm: PropTypes.bool,
  showHubspot: PropTypes.bool,
  existingEmails: PropTypes.instanceOf(Set),
  onSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
